// apps/provider/src/services/api/rtm.js
// Remote Therapeutic Monitoring (RTM) — enrollments, billing periods,
// management activities, interactions, dashboard, alerts, and patient list.
//
// Methods: createRTMEnrollment, getRTMEnrollments, getRTMEnrollment, updateRTMEnrollment,
//          getRTMBillingPeriods, getCurrentRTMBillingPeriod, getReadyToBillRTMPeriods, updateRTMBillingPeriod,
//          createRTMManagementActivity, getRTMManagementActivities,
//          createRTMInteraction, getRTMInteractions,
//          getRTMDashboard, getRTMAlerts, getRTMPatients
//
// Response types: @prism-ehr/shared → RTMEnrollment, RTMBillingPeriod, RTMManagementActivity,
//                 RTMInteraction, RTMDashboard, RTMAlert, RTMPatient, ReadyToBillPeriod

/**
 * @returns {Object} RTM domain methods (bound to `this` of the composed apiService)
 */
export function createRTMMethods() {
  return {

    // ============ RTM Enrollments ============

    /**
     * Create a new RTM enrollment for a patient.
     *
     * Xano: POST /rtm/enrollments
     * Xano staging file: 1041_rtm_enrollments_POST.xs
     *
     * Side effects: Sets status to "active" and enrolled_at to now server-side.
     *
     * @param {Object} data
     * @param {string} data.patient_id
     * @param {string} [data.patient_exercise_assignment_id]
     * @param {string} data.organization_id
     * @param {string} data.enrolled_by - Provider user ID
     * @param {boolean} [data.setup_education_completed]
     * @param {string} [data.payer_id]
     * @param {string} [data.payer_coverage_status]
     * @param {string} [data.notes]
     * @returns {Promise<import('@prism-ehr/shared').RTMEnrollment>}
     */
    createRTMEnrollment(data) {
      return this.request('/rtm/enrollments', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get RTM enrollments with optional filters.
     *
     * Xano: GET /rtm/enrollments
     * Xano staging file: 1040_rtm_enrollments_GET.xs
     *
     * Organization is derived from auth token server-side ($auth.extras.organization_id).
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.status] - e.g. "active", "discontinued"
     * @returns {Promise<import('@prism-ehr/shared').RTMEnrollment[]>}
     */
    getRTMEnrollments(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.status) queryParams.append('status', filters.status);

      const query = queryParams.toString();
      return this.request(`/rtm/enrollments${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single RTM enrollment by ID.
     *
     * Xano: GET /rtm/enrollments/{rtm_enrollment_id}
     * Xano staging file: 1039_rtm_enrollments_rtm_enrollment_id_GET.xs
     *
     * @param {string} enrollmentId
     * @returns {Promise<import('@prism-ehr/shared').RTMEnrollment>}
     */
    getRTMEnrollment(enrollmentId) {
      return this.request(`/rtm/enrollments/${enrollmentId}`, {
        method: 'GET'
      });
    },

    /**
     * Update an RTM enrollment (e.g. discontinue, update payer info).
     *
     * Xano: PATCH /rtm/enrollments/{rtm_enrollment_id}
     * Xano staging file: 1042_rtm_enrollments_rtm_enrollment_id_PATCH.xs
     *
     * @param {string} enrollmentId
     * @param {Partial<import('@prism-ehr/shared').RTMEnrollment>} data
     * @returns {Promise<import('@prism-ehr/shared').RTMEnrollment>}
     */
    updateRTMEnrollment(enrollmentId, data) {
      return this.request(`/rtm/enrollments/${enrollmentId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    // ============ RTM Billing Periods ============

    /**
     * Get RTM billing periods with optional filters.
     *
     * Xano: GET /rtm/billing-periods
     * Xano staging file: 1045_rtm_billing_periods_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.enrollment_id]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.status] - e.g. "open", "ready_to_bill", "billed"
     * @returns {Promise<import('@prism-ehr/shared').RTMBillingPeriod[]>}
     */
    getRTMBillingPeriods(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.enrollment_id) queryParams.append('enrollment_id', filters.enrollment_id);
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.status) queryParams.append('status', filters.status);

      const query = queryParams.toString();
      return this.request(`/rtm/billing-periods${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    /**
     * Get or create the current month's billing period for an enrollment.
     *
     * Xano: GET /rtm/billing-periods/current
     * Xano staging file: 1064_rtm_billing_periods_current_GET.xs
     *
     * Side effects: Auto-creates a new billing period if none exists for the
     * current month and the enrollment is active.
     *
     * @param {string} enrollmentId
     * @returns {Promise<import('@prism-ehr/shared').RTMBillingPeriod | null>}
     */
    getCurrentRTMBillingPeriod(enrollmentId) {
      return this.request(`/rtm/billing-periods/current?enrollment_id=${enrollmentId}`, {
        method: 'GET'
      });
    },

    /**
     * Get billing periods that are ready to bill for an organization.
     * Returns enriched periods with enrollment and patient info.
     *
     * Xano: GET /rtm/billing-periods/ready-to-bill
     * Xano staging file: 1065_rtm_billing_periods_ready_to_bill_GET.xs
     *
     * Organization is derived from auth token server-side ($auth.extras.organization_id).
     * Includes periods with status "ready_to_bill" and open periods past their
     * end date that have eligible codes.
     *
     * @returns {Promise<import('@prism-ehr/shared').ReadyToBillPeriod[]>}
     */
    getReadyToBillRTMPeriods() {
      return this.request('/rtm/billing-periods/ready-to-bill', {
        method: 'GET'
      });
    },

    /**
     * Update an RTM billing period (e.g. mark as billed, set claim_id).
     *
     * Xano: PATCH /rtm/billing-periods/{rtm_billing_period_id}
     * Xano staging file: 1066_rtm_billing_periods_rtm_billing_period_id_PATCH.xs
     *
     * Side effects: Auto-sets billed_at when status is changed to "billed".
     *
     * @param {string} periodId
     * @param {Object} data
     * @param {string} [data.status] - e.g. "billed", "ready_to_bill"
     * @param {string[]} [data.billed_codes]
     * @param {string} [data.claim_id]
     * @param {string} [data.billed_at]
     * @returns {Promise<import('@prism-ehr/shared').RTMBillingPeriod>}
     */
    updateRTMBillingPeriod(periodId, data) {
      return this.request(`/rtm/billing-periods/${periodId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    // ============ RTM Management Activities ============

    /**
     * Log a provider management activity against a billing period.
     *
     * Xano: POST /rtm/management-activities
     * Xano staging file: 1056_rtm_management_activities_POST.xs
     *
     * Side effects: Automatically adds duration (converted to minutes) to
     * the billing period's management_minutes_total.
     *
     * @param {Object} data
     * @param {string} data.rtm_enrollment_id
     * @param {string} data.rtm_billing_period_id
     * @param {string} data.provider_id
     * @param {string} [data.provider_designation]
     * @param {string} [data.activity_type]
     * @param {number} data.duration_seconds
     * @param {string} [data.started_at]
     * @param {string} [data.ended_at]
     * @param {string} [data.notes]
     * @returns {Promise<import('@prism-ehr/shared').RTMManagementActivity>}
     */
    createRTMManagementActivity(data) {
      return this.request('/rtm/management-activities', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get management activities with optional filters.
     *
     * Xano: GET /rtm/management-activities
     * Xano staging file: 1055_rtm_management_activities_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.enrollment_id]
     * @param {string} [filters.period_id]
     * @param {string} [filters.provider_id]
     * @returns {Promise<import('@prism-ehr/shared').RTMManagementActivity[]>}
     */
    getRTMManagementActivities(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.enrollment_id) queryParams.append('enrollment_id', filters.enrollment_id);
      if (filters.period_id) queryParams.append('period_id', filters.period_id);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);

      const query = queryParams.toString();
      return this.request(`/rtm/management-activities${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    // ============ RTM Interactions ============

    /**
     * Log an interactive communication for RTM billing.
     *
     * Xano: POST /rtm/interactions
     * Xano staging file: 1061_rtm_interactions_POST.xs
     *
     * Side effects: When qualifies_for_rtm_billing is true, sets
     * interactive_communication_completed=true on the billing period.
     *
     * @param {Object} data
     * @param {string} data.rtm_enrollment_id
     * @param {string} data.rtm_billing_period_id
     * @param {string} [data.interaction_type]
     * @param {string} [data.interaction_date]
     * @param {number} [data.duration_minutes]
     * @param {string} data.provider_id
     * @param {string} [data.appointment_id]
     * @param {string} [data.notes]
     * @param {boolean} [data.qualifies_for_rtm_billing=true]
     * @returns {Promise<import('@prism-ehr/shared').RTMInteraction>}
     */
    createRTMInteraction(data) {
      return this.request('/rtm/interactions', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get interactions with optional filters.
     *
     * Xano: GET /rtm/interactions
     * Xano staging file: 1060_rtm_interactions_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.enrollment_id]
     * @param {string} [filters.period_id]
     * @returns {Promise<import('@prism-ehr/shared').RTMInteraction[]>}
     */
    getRTMInteractions(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.enrollment_id) queryParams.append('enrollment_id', filters.enrollment_id);
      if (filters.period_id) queryParams.append('period_id', filters.period_id);

      const query = queryParams.toString();
      return this.request(`/rtm/interactions${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    // ============ RTM Dashboard & Alerts ============

    /**
     * Get aggregated RTM dashboard stats for an organization.
     *
     * Xano: GET /rtm/dashboard
     * Xano staging file: 1067_rtm_dashboard_GET.xs
     *
     * Organization is derived from auth token server-side ($auth.extras.organization_id).
     * Returns active enrollment count, ready-to-bill count, missing interaction
     * count, estimated revenue (using 2026 Medicare rates), and period status breakdown.
     *
     * @returns {Promise<import('@prism-ehr/shared').RTMDashboard>}
     */
    getRTMDashboard() {
      return this.request('/rtm/dashboard', {
        method: 'GET'
      });
    },

    /**
     * Get actionable RTM alerts for an organization.
     *
     * Xano: GET /rtm/alerts
     * Xano staging file: 1068_rtm_alerts_GET.xs
     *
     * Organization is derived from auth token server-side ($auth.extras.organization_id).
     * Alert types: approaching_threshold, missing_interaction,
     * payer_not_covered, low_engagement, near_management_threshold.
     *
     * @returns {Promise<import('@prism-ehr/shared').RTMAlert[]>}
     */
    getRTMAlerts() {
      return this.request('/rtm/alerts', {
        method: 'GET'
      });
    },

    // ============ RTM Patients ============

    /**
     * Get all RTM patients with enrollment, program, and current period info.
     *
     * Xano: GET /rtm/patients
     * Xano staging file: 1069_rtm_patients_GET.xs
     *
     * Organization is derived from auth token server-side ($auth.extras.organization_id).
     * Returns enriched patient rows with nested current_period data.
     * Joins across: rtm_enrollment, patient, patient_exercise_assignment,
     * rehab_program, rtm_billing_period.
     *
     * @returns {Promise<import('@prism-ehr/shared').RTMPatient[]>}
     */
    getRTMPatients() {
      return this.request('/rtm/patients', {
        method: 'GET'
      });
    },

  };
}
